export type Lang = 'de' | 'en';

const strings = {
  de: {
    title: 'Murmurations',
    controls: 'Einstellungen',
    basic: 'Einfach',
    expert: 'Experte',
    share: 'Link kopieren',
    shared: 'Link kopiert',
    shareFailed: 'Kopieren fehlgeschlagen. Der Link steht in der Adresszeile.',
    horizon: 'Horizont setzen',
    horizonHint: 'Ziehe die Linie auf den Horizont des Fotos. Tippe, wenn sie passt.',
    reseed: 'Neuer Schwarm',
    pause: 'Pause',
    play: 'Weiter',
    photo: 'Foto',
    language: 'English',
    noWebGL: 'Dieser Browser kann die Simulation nicht anzeigen. WebGL 2 mit Gleitkomma-Texturen wird benötigt.',
    badLink: 'Dieser Link konnte nicht gelesen werden. Es wird eine Standardszene gezeigt.',
    photoBy: 'Foto',
    kiosk: 'Kiosk',
    kioskHintTouch: 'Kiosk-Modus. Mit zwei Fingern tippen beendet ihn.',
    kioskHintMouse: 'Kiosk-Modus. Doppelklick oder Esc beendet ihn.',
    kioskHintTap: 'Kiosk-Modus. Tippen oder eine Taste beendet ihn.',
    liveHintTouch: 'Finger auf den Schwarm legen und halten: ein Falke greift an.',
    liveHintMouse: 'Maustaste auf dem Schwarm gedrückt halten: ein Falke greift an.',
    fullscreen: 'Vollbild',
    flagLive: 'Berührung ruft den Falken',
    flagHideControls: 'Bedienelemente im Link ausblenden',
    hudToggle: 'Leistungsanzeige',
    photoFailed: 'Das Foto konnte nicht geladen werden.',
    lightFrom: { back: 'Gegenlicht', right: 'von rechts', front: 'im Rücken', left: 'von links' },
    param: {
      count: 'Anzahl Vögel',
      cohesion: 'Zusammenhalt',
      separation: 'Abstand',
      alignment: 'Gleichrichtung',
      speed: 'Geschwindigkeit',
      nervousness: 'Nervosität',
      predatorRate: 'Falke, Häufigkeit',
      predatorAggression: 'Falke, Aggression',
      horizon: 'Horizont',
      horizonTilt: 'Horizont, Neigung',
      lightAngle: 'Licht, Richtung',
      lightWarmth: 'Licht, Wärme',
      haze: 'Dunst',
      birdSize: 'Vogelgröße',
      flockHeight: 'Flughöhe',
      flockDistance: 'Entfernung',
      flockSpread: 'Ausdehnung',
      turnRate: 'Wendigkeit',
      flapRate: 'Flügelschlag',
      crowding: 'Dichte',
      lightAuto: 'Licht vom Foto',
    },
  },
  en: {
    title: 'Murmurations',
    controls: 'Settings',
    basic: 'Basic',
    expert: 'Expert',
    share: 'Copy link',
    shared: 'Link copied',
    shareFailed: 'Copy failed. The link is in the address bar.',
    horizon: 'Set horizon',
    horizonHint: 'Drag the line onto the horizon of the photo. Tap when it fits.',
    reseed: 'New flock',
    pause: 'Pause',
    play: 'Play',
    photo: 'Photo',
    language: 'Deutsch',
    noWebGL: 'This browser cannot show the simulation. WebGL 2 with float textures is required.',
    badLink: 'This link could not be read. Showing a default scene instead.',
    photoBy: 'Photo',
    kiosk: 'Kiosk',
    kioskHintTouch: 'Kiosk mode. Two-finger tap to leave.',
    kioskHintMouse: 'Kiosk mode. Double-click or Esc to leave.',
    kioskHintTap: 'Kiosk mode. Tap or any key to leave.',
    liveHintTouch: 'Touch and hold the flock: a falcon attacks.',
    liveHintMouse: 'Press and hold on the flock: a falcon attacks.',
    fullscreen: 'Fullscreen',
    flagLive: 'Touch summons the falcon',
    flagHideControls: 'Hide controls in the link',
    hudToggle: 'Performance overlay',
    photoFailed: 'The photo could not be loaded.',
    lightFrom: { back: 'backlit', right: 'from the right', front: 'from behind you', left: 'from the left' },
    param: {
      count: 'Number of birds',
      cohesion: 'Cohesion',
      separation: 'Separation',
      alignment: 'Alignment',
      speed: 'Speed',
      nervousness: 'Nervousness',
      predatorRate: 'Falcon, frequency',
      predatorAggression: 'Falcon, aggression',
      horizon: 'Horizon',
      horizonTilt: 'Horizon tilt',
      lightAngle: 'Light direction',
      lightWarmth: 'Light warmth',
      haze: 'Haze',
      birdSize: 'Bird size',
      flockHeight: 'Altitude',
      flockDistance: 'Distance',
      flockSpread: 'Spread',
      turnRate: 'Agility',
      flapRate: 'Wing beat',
      crowding: 'Density',
      lightAuto: 'Light from the photo',
    },
  },
} as const;

export type Strings = (typeof strings)['en'];
type PlainKey = Exclude<keyof Strings, 'param' | 'lightFrom'>;
type ParamKey = keyof Strings['param'];
type LightFromKey = keyof Strings['lightFrom'];

let current: Lang = detectLang();

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem('murmurations.lang');
    if (stored === 'de' || stored === 'en') return stored;
  } catch {
    /* storage may be unavailable */
  }
  const nav = (navigator.languages?.[0] ?? navigator.language ?? 'en').toLowerCase();
  return nav.startsWith('de') ? 'de' : 'en';
}

export function lang(): Lang {
  return current;
}

export function setLang(l: Lang): void {
  current = l;
  try {
    localStorage.setItem('murmurations.lang', l);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = l;
}

export function toggleLang(): Lang {
  setLang(current === 'de' ? 'en' : 'de');
  return current;
}

export function t(key: PlainKey): string {
  return strings[current][key];
}

export function tParam(key: ParamKey): string {
  return strings[current].param[key];
}

/** A word for a light azimuth: four quadrants around ahead, right, behind, left. */
export function tLightFrom(angleDeg: number): string {
  const a = ((angleDeg % 360) + 360) % 360;
  const key: LightFromKey = a < 45 || a >= 315 ? 'back' : a < 135 ? 'right' : a < 225 ? 'front' : 'left';
  return strings[current].lightFrom[key];
}

/** Coarse pointers pick the touch wording; everything else the mouse wording. */
export function isTouchDevice(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}
